import { useOverwatchWidgets } from 'api/overwatch';
import { FEATURES } from 'appConstants';
import useTenantConfigPending from 'containers/Intelligence/useTenantConfigPending';
import { useFeatureFlags } from 'hooks';

import useOverwatchAccess from '../permissions/useOverwatchAccess';
import { IOverwatchWidgetDefinition } from './types';

export type OverwatchVisibility = 'loading' | 'visible' | 'hidden';

export interface IOverwatchVisibilityResult {
  visibility: OverwatchVisibility;
  widgets: IOverwatchWidgetDefinition[];
}

/**
 * Both tenant features, at least one role-granted screen, and a non-empty discovery list: the API
 * answers an empty list, not a 403, when the caller may see nothing.
 */
export const useOverwatchVisibility = (): IOverwatchVisibilityResult => {
  const flags = useFeatureFlags();
  const tenantPending = useTenantConfigPending();
  const access = useOverwatchAccess();

  const featuresOn =
    flags[FEATURES.PERX_AI_WORKSPACE.key] === true &&
    flags[FEATURES.LAYER_6_DASHBOARD.key] === true;
  const roleAllows = access.businessOverview || access.systemSupport;
  const { widgets, isLoading, error } = useOverwatchWidgets(featuresOn && roleAllows);

  if (tenantPending) {
    return { visibility: 'loading', widgets: [] };
  }

  if (!featuresOn || !roleAllows || error) {
    return { visibility: 'hidden', widgets: [] };
  }

  if (isLoading || widgets === undefined) {
    return { visibility: 'loading', widgets: [] };
  }

  return { visibility: widgets.length > 0 ? 'visible' : 'hidden', widgets };
};

export default useOverwatchVisibility;
